import { useAuth } from '@clerk/react';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import {
  useConnectEmailAccountMutation,
  useGetConnectOutcomeQuery,
} from '@/store/features/engage.feature';
import { apiErrorMessage } from '../api-error.util';
import { useSenderAccounts } from '../use-sender-accounts.hook';
import {
  CHOOSE,
  CONNECT_CHANNEL,
  connectReducer,
  findConnected,
  isConnectReturn,
  isWaiting,
  popupFeatures,
  snapshotMailboxes,
  type ConnectProvider,
} from './connect-mailbox.util';

const WINDOW_NAME = 'cleanlist-mailbox-connect';
/** How often the dialog asks whether the address has landed. */
const POLL_MS = 2000;
/** After the page reports back, how long the address may take to land. */
const LAND_MS = 20_000;
/** A sign-in window left open this long was abandoned. */
const ABANDON_MS = 5 * 60_000;

/**
 * The connect flow's logic: opens the provider's sign-in in a window, and
 * waits for the new mailbox to land (the API's outcome, with the mailbox list
 * as the fallback) without leaving the page. If the browser blocks the window
 * it falls back to the hosted redirect, which returns to Settings.
 */
export function useConnectMailbox({ open }: { open: boolean }) {
  const { userId } = useAuth();
  const [phase, dispatch] = useReducer(connectReducer, CHOOSE);
  const [connect] = useConnectEmailAccountMutation();
  const { email: mailboxes, refetch } = useSenderAccounts();
  const popup = useRef<Window | null>(null);
  const link = useRef('');
  // The mailboxes before the sign-in; null when the list was not loaded yet,
  // which leaves the outcome as the only way to see the address land.
  const before = useRef<Map<string, boolean> | null>(null);
  const started = useRef(false);
  const mounted = useRef(true);
  const generation = useRef(0);
  // The server clock the outcome is read from; null when the API gave none.
  const [since, setSince] = useState<number | null>(null);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const waiting = isWaiting(phase);
  const missingMailbox =
    (phase.kind === 'checks' || phase.kind === 'already') &&
    !mailboxes.some((mailbox) => mailbox.id === phase.mailboxId);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      popup.current?.close();
    };
  }, []);

  // Closing the dialog ends the attempt; the list is read once more so a
  // mailbox that landed late is not missing from the page behind it.
  useEffect(() => {
    if (open) return;
    generation.current++;
    popup.current?.close();
    popup.current = null;
    dispatch({ type: 'restart' });
    setSince(null);
    setAttemptId(null);
    if (started.current) void refetch();
    started.current = false;
  }, [open, refetch]);

  const outcome = useGetConnectOutcomeQuery(
    { since: since ?? 0, ...(attemptId ? { attempt_id: attemptId } : {}) },
    { skip: !waiting || since === null, pollingInterval: POLL_MS }
  );
  useEffect(() => {
    const current = outcome.currentData;
    if (
      waiting &&
      since !== null &&
      current &&
      current.status !== 'pending' &&
      current.at >= since &&
      (!attemptId || current.attempt_id === attemptId)
    )
      dispatch({ type: 'outcome', outcome: current });
  }, [outcome.currentData, waiting, since, attemptId]);

  // An outcome may arrive before the list response containing its row.
  // Keep reading until Checks can show that exact mailbox.
  useEffect(() => {
    if (!waiting && !missingMailbox) return;
    const poll = window.setInterval(() => void refetch(), POLL_MS);
    return () => window.clearInterval(poll);
  }, [waiting, missingMailbox, refetch]);
  useEffect(() => {
    // Never race the correlated server answer with an org-wide list change.
    if (
      !waiting ||
      attemptId ||
      !before.current ||
      (since !== null && !outcome.isError)
    )
      return;
    const landed = findConnected(before.current, mailboxes, userId);
    if (landed) dispatch({ type: 'mailbox', mailboxId: landed.id });
  }, [waiting, mailboxes, since, outcome.isError, userId, attemptId]);

  // The return page's word that the window finished (or did not).
  useEffect(() => {
    if (!waiting || typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(CONNECT_CHANNEL);
    channel.onmessage = (event: MessageEvent) => {
      if (
        isConnectReturn(event.data) &&
        (event.data.attemptId ?? null) === attemptId
      )
        dispatch({ type: 'windowReturned', ok: event.data.ok });
    };
    return () => channel.close();
  }, [waiting, attemptId]);

  const returned = phase.kind === 'waiting' && phase.returned;
  const waitingOnWindow = phase.kind === 'waiting' && !phase.returned;
  // Provider pages can sever the opener and report closed while sign-in is
  // still open. Only a return/outcome or the abandon timer ends this wait.
  useEffect(() => {
    if (!waitingOnWindow) return;
    const abandon = window.setTimeout(
      () => dispatch({ type: 'gaveUp' }),
      ABANDON_MS
    );
    return () => window.clearTimeout(abandon);
  }, [waitingOnWindow]);
  // The provider said it finished; the address has this long to show up.
  useEffect(() => {
    if (!returned) return;
    const wait = window.setTimeout(() => dispatch({ type: 'gaveUp' }), LAND_MS);
    return () => window.clearTimeout(wait);
  }, [returned]);

  /**
   * Starts a sign-in. Call it from the click itself: the window has to be
   * opened in the gesture or the browser blocks it.
   */
  const pick = useCallback(
    (provider: ConnectProvider) => {
      const ownGeneration = ++generation.current;
      started.current = true;
      before.current = null;
      setSince(null);
      setAttemptId(null);
      const win = window.open('', WINDOW_NAME, popupFeatures());
      popup.current = win;
      dispatch({ type: 'pick', provider });
      void (async () => {
        try {
          // Snapshot a fresh response; a cached list can predate another
          // connection by minutes. Failure leaves only the outcome usable.
          try {
            const fresh = await refetch();
            before.current = fresh.data ? snapshotMailboxes(fresh.data) : null;
          } catch {
            before.current = null;
          }
          if (!mounted.current || ownGeneration !== generation.current)
            return win?.close();
          const result = await connect({
            provider,
            popup: win !== null,
          }).unwrap();
          if (!mounted.current || ownGeneration !== generation.current)
            return win?.close();
          link.current = result.authorize_url;
          if (!win) {
            // Blocked: the hosted page replaces this one and returns to
            // Settings, as the connect always did.
            window.location.href = result.authorize_url;
            return;
          }
          setSince(result.started_at ?? null);
          setAttemptId(result.attempt_id ?? null);
          win.location.href = result.authorize_url;
          dispatch({ type: 'opened' });
        } catch (error) {
          win?.close();
          popup.current = null;
          if (mounted.current && ownGeneration === generation.current)
            dispatch({
              type: 'startFailed',
              refused: (error as { status?: unknown })?.status === 403,
              message:
                apiErrorMessage(error) ||
                'Could not start the connection. Try again.',
            });
        }
      })();
    },
    [connect, refetch]
  );

  /** Brings the sign-in window forward, or opens it again from the same link. */
  const reopen = useCallback(() => {
    if (popup.current && !popup.current.closed) {
      popup.current.focus();
      return;
    }
    const win = window.open(link.current, WINDOW_NAME, popupFeatures());
    if (win) popup.current = win;
    else if (link.current) window.location.href = link.current;
  }, []);

  /** Asks again whether the address has landed, and gives it more time. */
  const recheck = useCallback(() => {
    dispatch({ type: 'recheck' });
    void refetch();
    if (since !== null) void outcome.refetch();
  }, [refetch, since, outcome]);

  const restart = useCallback(() => {
    generation.current++;
    setSince(null);
    setAttemptId(null);
    dispatch({ type: 'restart' });
  }, []);

  return { phase, mailboxes, pick, reopen, recheck, restart };
}
